import { React, useState, useRef, useEffect, useContext } from "react";
import { LoginContext } from "../../../contexts/LoginContext";
import {
  fetchUserData,
  fetchEditProfileData,
  updateUserDetails,
  updateUserPassword,
} from "./api-handlers";
import routes from "../../../config/routes-config";
import { validateDetails, validatePasswords } from "./validate-form";
import { EyeIcon, EyeOffIcon, PencilIcon } from "@heroicons/react/solid";
import { XIcon } from "@heroicons/react/outline";
import AlertModal from "../../../components/teacher/AlertModal";

const EditProfile = () => {
  // -----------------------------------
  // Fetch Profile Data
  // -----------------------------------

  const { userAuthStatus, user } = useContext(LoginContext);

  const [userInfo, setUserInfo] = useState({
    id: "",
    name: "",
    department: "",
    collegeEmailId: "",
    profilePictureLink: "",
  });

  useEffect(() => {
    if (userAuthStatus && user !== null) {
      fetchUserData(
        routes.teacher.profileDetails.userInfoUrl +
          "?teacherUsername=" +
          user.userName,
        setUserInfo,
        user.userName,
        user.token
      );
    }
  }, [userAuthStatus, user]);

  // -----------------------------------
  // Password related data and functions
  // -----------------------------------
  const [changePassword, setChangePassword] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
    commonError: "",
  });

  const [isPasswordModalOpened, setPasswordModalOpened] = useState(false);

  const [showPassword, setToggle] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const input = {
    oldPassword: useRef(null),
    newPassword: useRef(null),
    confirmPassword: useRef(null),
  };

  const togglePassword = (field) => {
    var toggleStates = {};
    Object.assign(toggleStates, showPassword);
    toggleStates[field] = !toggleStates[field];
    setToggle(toggleStates);
    input[field].current.focus();
  };

  useEffect(() => {
    Object.keys(input).forEach((field) => {
      input[field].current.selectionStart = input[field].current.value.length;
      input[field].current.selectionEnd = input[field].current.value.length;
    });
  });

  const handlePasswordInput = (event) => {
    setChangePassword({
      ...changePassword,
      [event.target.name]: event.target.value,
    });
  };

  const onSubmitPassword = async (event) => {
    event.preventDefault();

    setErrors(validatePasswords(changePassword));

    const payload = {
      username: user.userName,
      oldPassword: changePassword.oldPassword,
      newPassword: changePassword.newPassword,
    };

    updateUserPassword(
      routes.teacher.profileDetails.changePasswordUrl,
      user.token,
      payload,
      validatePasswords(changePassword),
      setErrors
    ).then((status) => {
      if (status) {
        setPasswordModalOpened(true);
        setChangePassword({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      }
    });
  };

  // -----------------------------------
  // Details related data and functions
  // -----------------------------------

  const [changeDetails, setDetails] = useState({
    personalEmail: "",
    linkedin: "",
    github: "",
    twitter: "",
    bio: "",
  });

  useEffect(() => {
    if (userAuthStatus && user !== null) {
      fetchEditProfileData(
        routes.teacher.profileDetails.fetchUserDetailsUrl +
          "?username=" +
          user.userName,
        setDetails,
        user.token
      );
    }
  }, [userAuthStatus, user]);

  const [editable, setEditable] = useState(false);
  const [currentState, setCurrentState] = useState({});

  const toggleEditable = (input) => {
    if (!editable) {
      setCurrentState(input);
    }
    setEditable(!editable);
    if (editable) {
      setDetails(currentState);
    }
  };

  const [detailErrors, setDetailErrors] = useState({
    personalEmail: "",
    linkedin: "",
    github: "",
    twitter: "",
    bio: "",
    commonError: "",
  });

  const handleDetailsInput = (event) => {
    setDetails({
      ...changeDetails,
      [event.target.name]: event.target.value,
    });
  };

  const onSubmitDetails = async (event) => {
    event.preventDefault();

    setDetailErrors(validateDetails(changeDetails));

    const user = JSON.parse(sessionStorage.getItem("auth"));

    const payload = {
      personalEmailId: changeDetails.personalEmail,
      linkedInProfile: changeDetails.linkedin,
      githubProfile: changeDetails.github,
      twitterProfile: changeDetails.twitter,
      bio: changeDetails.bio,
      id: user.userName,
    };

    updateUserDetails(
      routes.teacher.profileDetails.changeUserDetailsUrl,
      user.token,
      payload,
      validateDetails(changeDetails),
      setDetailErrors
    ).then((status) => {
      if (status) {
        setDetailsModalOpened(true);
      }
    });
  };

  const [isDetailsModalOpened, setDetailsModalOpened] = useState(false);

  return (
    <>
      <div className="bg-[#ECECEC] w-screen flex flex-col px-24 py-8 lg:justify-between items-center lg:items-start">
        <h1 className="lg:mb-0 mb-10 text-3xl font-semibold text-[#1E1E4A]">
          Profile Details
        </h1>
        <div className="grid lg:grid-cols-7 gap-6 lg:w-full w-96 lg:h-[calc(100vh-8rem)] h-[calc(150vh)]">
          <div className="lg:col-span-3 h-full">
            <div className="grid grid-rows-3 gap-6 h-full">
              <div className="row-span-1 bg-white rounded-2xl lg:w-full p-8">
                <div className="flex flex-row justify-around place-items-center h-full">
                  <div className="flex flex-col items-center w-1/4">
                    <img
                      className="inline object-cover max-h-28 w-28 rounded-full"
                      src={userInfo.profilePictureLink}
                      alt="Profile"
                    />
                  </div>
                  <div className="flex flex-col w-3/4 font-medium text-left lg:pl-8">
                    <div className="flex flex-row justify-between">
                      <h1 className="w-1/4 mr-2 text-[#1C42D5]">Name:</h1>
                      <h1 className="w-3/4">{userInfo.name} </h1>
                    </div>
                    <div className="flex flex-row justify-between">
                      <h1 className="w-1/4 mr-2 text-[#1C42D5]">Emp ID:</h1>
                      <h1 className="w-3/4">{userInfo.id}</h1>
                    </div>
                    <div className="flex flex-row justify-between">
                      <h1 className="w-1/4 mr-2 text-[#1C42D5]">Dept:</h1>
                      <h1 className="w-3/4">{userInfo.department}</h1>
                    </div>
                    <div className="flex flex-row justify-between">
                      <h1 className="w-1/4 mr-2 text-[#1C42D5]">Email:</h1>
                      <h1 className="w-3/4 break-words">
                        {userInfo.collegeEmailId}
                      </h1>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row-span-2 bg-white rounded-2xl lg:w-auto p-8">
                <div className="flex flex-col w-full h-full">
                  <h1 className="font-medium text-2xl text-[#5C5CC0]">
                    Change Password
                  </h1>
                  <div className="my-4 w-full bg-gray-200 h-1"></div>
                  <form
                    className="h-full flex flex-col justify-between items-center"
                    onSubmit={onSubmitPassword}
                  >
                    <div className="lg:w-2/3 w-5/6">
                      <label
                        className="block text-[#777777]"
                        htmlFor="old-password"
                      >
                        Old Password
                      </label>
                      <div className="relative w-full">
                        <div className="absolute inset-y-0 right-0 flex items-center px-2">
                          {showPassword.oldPassword ? (
                            <EyeIcon
                              className="h-5 fill-slate-500"
                              onClick={() => togglePassword("oldPassword")}
                            />
                          ) : (
                            !showPassword.oldPassword && (
                              <EyeOffIcon
                                className="h-5 fill-slate-500"
                                onClick={() => togglePassword("oldPassword")}
                              />
                            )
                          )}
                        </div>
                        <input
                          className="bg-[#E4E6FF] border-2 rounded-lg w-full py-1 px-4 text-gray-700 leading-none focus:bg-white border-transparent focus:border-transparent focus:ring-1"
                          id="old-password"
                          type={showPassword.oldPassword ? "text" : "password"}
                          name="oldPassword"
                          value={changePassword.oldPassword}
                          onChange={handlePasswordInput}
                          autoComplete="off"
                          ref={input["oldPassword"]}
                        />
                      </div>
                      {errors.oldPassword !== "" && (
                        <h1 className="text-xs text-red-500">
                          {errors.oldPassword}
                        </h1>
                      )}
                    </div>
                    <div className="lg:w-2/3 w-5/6">
                      <label
                        className="block text-[#777777]"
                        htmlFor="new-password"
                      >
                        New Password
                      </label>
                      <div className="relative w-full">
                        <div className="absolute inset-y-0 right-0 flex items-center px-2">
                          {showPassword.newPassword ? (
                            <EyeIcon
                              className="h-5 fill-slate-500"
                              onClick={() => togglePassword("newPassword")}
                            />
                          ) : (
                            !showPassword.newPassword && (
                              <EyeOffIcon
                                className="h-5 fill-slate-500"
                                onClick={() => togglePassword("newPassword")}
                              />
                            )
                          )}
                        </div>
                        <input
                          className="bg-[#E4E6FF] border-2 rounded-lg w-full py-1 px-4 text-gray-700 leading-none focus:bg-white border-transparent focus:border-transparent focus:ring-1"
                          id="new-password"
                          type={showPassword.newPassword ? "text" : "password"}
                          name="newPassword"
                          value={changePassword.newPassword}
                          onChange={handlePasswordInput}
                          autoComplete="off"
                          ref={input["newPassword"]}
                        />
                      </div>
                      {errors.newPassword !== "" && (
                        <h1 className="text-xs text-red-500">
                          {errors.newPassword}
                        </h1>
                      )}
                    </div>
                    <div className="lg:w-2/3 w-5/6">
                      <label
                        className="block text-[#777777]"
                        htmlFor="confirm-password"
                      >
                        Retype New Password
                      </label>
                      <div className="relative w-full">
                        <div className="absolute inset-y-0 right-0 flex items-center px-2">
                          {showPassword.confirmPassword ? (
                            <EyeIcon
                              className="h-5 fill-slate-500"
                              onClick={() => togglePassword("confirmPassword")}
                            />
                          ) : (
                            !showPassword.confirmPassword && (
                              <EyeOffIcon
                                className="h-5 fill-slate-500"
                                onClick={() =>
                                  togglePassword("confirmPassword")
                                }
                              />
                            )
                          )}
                        </div>
                        <input
                          className="bg-[#E4E6FF] border-2 rounded-lg w-full py-1 px-4 text-gray-700 leading-none focus:bg-white border-transparent focus:border-transparent focus:ring-1"
                          id="confirm-password"
                          type={
                            showPassword.confirmPassword ? "text" : "password"
                          }
                          name="confirmPassword"
                          value={changePassword.confirmPassword}
                          onChange={handlePasswordInput}
                          autoComplete="off"
                          ref={input["confirmPassword"]}
                        />
                      </div>
                      {errors.confirmPassword !== "" && (
                        <h1 className="text-xs text-red-500">
                          {errors.confirmPassword}
                        </h1>
                      )}
                      {errors.commonError !== "" && (
                        <h1 className="text-xs text-red-500">
                          {errors.commonError}
                        </h1>
                      )}
                    </div>

                    <button className="bg-[#6767ce] hover:bg-[#4242A8] text-white font-bold mt-6 py-1.5 px-4 rounded-full lg:w-5/12 w-7/12">
                      Change Password
                    </button>
                    <AlertModal
                      title={"Password Changed!"}
                      show={isPasswordModalOpened}
                      onCloseCallback={() => setPasswordModalOpened(false)}
                    />
                  </form>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-4 lg:h-full h-[calc(75vh)] bg-white rounded-2xl lg:w-auto p-8">
            <div className="flex flex-col w-full h-full">
              <div className="flex flex-row place-items-center justify-between">
                <h1 className="font-medium text-2xl text-[#5C5CC0]">
                  Change Details
                </h1>
                {!editable ? (
                  <button
                    className="ml-2 bg-transparent hover:bg-[#5C5CC0] text-[#5C5CC0] font-semibold hover:text-white px-2 border border-[#5C5CC0] hover:border-transparent rounded"
                    onClick={() => toggleEditable(changeDetails)}
                  >
                    <div className="flex flex-row place-items-center">
                      <PencilIcon className="h-5" />
                      <h1>Edit</h1>
                    </div>
                  </button>
                ) : (
                  <button
                    className="ml-2 bg-transparent hover:bg-[#5C5CC0] text-[#5C5CC0] font-semibold hover:text-white px-2 border border-[#5C5CC0] hover:border-transparent rounded"
                    onClick={() => toggleEditable(changeDetails)}
                  >
                    <div className="flex flex-row place-items-center">
                      <XIcon className="h-5" />
                      <h1>Cancel</h1>
                    </div>
                  </button>
                )}
              </div>
              <div className="my-4 w-full bg-gray-200 h-1"></div>
              <div className="h-max flex flex-col pb-4 text-[#AAAAAA] text-xs">
                <h1 className="">
                  Enter full website for social media accounts.
                </h1>
                <h1 className="">
                  For example “https://twitter.com/xyz” or
                  “https://www.linkedin.com/in/xyz/”
                </h1>
              </div>
              <form
                className="h-full flex flex-col justify-between items-center"
                onSubmit={onSubmitDetails}
              >
                <div className="w-5/6">
                  <label
                    className="block text-[#777777]"
                    htmlFor="personalEmail"
                  >
                    Personal Email ID
                  </label>
                  <input
                    className={`border-2 rounded-lg w-full py-1 px-4 text-gray-700 leading-none focus:bg-white border-transparent focus:border-transparent focus:ring-1 ${
                      !editable ? "bg-gray-300" : "bg-[#E4E6FF]"
                    }`}
                    id="personalEmail"
                    type="text"
                    name="personalEmail"
                    value={changeDetails.personalEmail}
                    onChange={handleDetailsInput}
                    autoComplete="off"
                    disabled={!editable}
                  />
                  {detailErrors.personalEmail !== "" && (
                    <h1 className="text-xs text-red-500">
                      {detailErrors.personalEmail}
                    </h1>
                  )}
                </div>
                <div className="w-5/6">
                  <label className="block text-[#777777]" htmlFor="linkedin">
                    LinkedIn Profile
                  </label>
                  <input
                    className={`border-2 rounded-lg w-full py-1 px-4 text-gray-700 leading-none focus:bg-white border-transparent focus:border-transparent focus:ring-1 ${
                      !editable ? "bg-gray-300" : "bg-[#E4E6FF]"
                    }`}
                    id="linkedin"
                    type="text"
                    name="linkedin"
                    value={changeDetails.linkedin}
                    onChange={handleDetailsInput}
                    autoComplete="off"
                    disabled={!editable}
                  />
                  {detailErrors.linkedin !== "" && (
                    <h1 className="text-xs text-red-500">
                      {detailErrors.linkedin}
                    </h1>
                  )}
                </div>
                <div className="w-5/6">
                  <label
                    className="block text-[#777777]"
                    htmlFor="old-password"
                  >
                    GitHub Profile
                  </label>
                  <input
                    className={`border-2 rounded-lg w-full py-1 px-4 text-gray-700 leading-none focus:bg-white border-transparent focus:border-transparent focus:ring-1 ${
                      !editable ? "bg-gray-300" : "bg-[#E4E6FF]"
                    }`}
                    id="github"
                    type="text"
                    name="github"
                    value={changeDetails.github}
                    onChange={handleDetailsInput}
                    autoComplete="off"
                    disabled={!editable}
                  />
                  {detailErrors.github !== "" && (
                    <h1 className="text-xs text-red-500">
                      {detailErrors.github}
                    </h1>
                  )}
                </div>
                <div className="w-5/6">
                  <label className="block text-[#777777]" htmlFor="twitter">
                    Twitter Profile
                  </label>
                  <input
                    className={`border-2 rounded-lg w-full py-1 px-4 text-gray-700 leading-none focus:bg-white border-transparent focus:border-transparent focus:ring-1 ${
                      !editable ? "bg-gray-300" : "bg-[#E4E6FF]"
                    }`}
                    id="twitter"
                    type="text"
                    name="twitter"
                    value={changeDetails.twitter}
                    onChange={handleDetailsInput}
                    autoComplete="off"
                    disabled={!editable}
                  />
                  {detailErrors.twitter !== "" && (
                    <h1 className="text-xs text-red-500">
                      {detailErrors.twitter}
                    </h1>
                  )}
                </div>
                <div className="w-5/6">
                  <label className="block text-[#777777]" htmlFor="bio">
                    Bio
                  </label>
                  <input
                    className={`border-2 rounded-lg w-full py-1 px-4 text-gray-700 leading-none focus:bg-white border-transparent focus:border-transparent focus:ring-1 ${
                      !editable ? "bg-gray-300" : "bg-[#E4E6FF]"
                    }`}
                    id="bio"
                    type="text"
                    name="bio"
                    value={changeDetails.bio}
                    onChange={handleDetailsInput}
                    autoComplete="off"
                    disabled={!editable}
                  />
                  {detailErrors.bio !== "" && (
                    <h1 className="text-xs text-red-500">{detailErrors.bio}</h1>
                  )}
                  {detailErrors.commonError !== "" && (
                    <h1 className="text-xs text-red-500">
                      {detailErrors.commonError}
                    </h1>
                  )}
                </div>
                <button
                  disabled={!editable}
                  className={`text-white font-bold mt-6 py-1.5 px-4 rounded-full lg:w-5/12 w-7/12 ${
                    !editable
                      ? "bg-[#b4b4e9]"
                      : "bg-[#6767ce] hover:bg-[#4242A8]"
                  }`}
                >
                  Update Profile
                </button>
                <AlertModal
                  title={"Details Updated!"}
                  show={isDetailsModalOpened}
                  onCloseCallback={() => setDetailsModalOpened(false)}
                />
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditProfile;
